import React, { useMemo } from "react";
import { Mail, MapPin, Phone, ShieldCheck, Sparkles, X } from "lucide-react";
import ModalOverlay from "../UI/ModalOverlay/ModalOverlay.tsx";
import { PeopleSearchProfile } from "../../types/peopleSearch.ts";
import styles from "./PeopleSearchWorkerModal.module.scss";

type PeopleSearchWorkerModalProps = {
  isOpen: boolean;
  worker: PeopleSearchProfile | null;
  onClose: () => void;
  onContact?: (profile: PeopleSearchProfile) => void;
};

const PeopleSearchWorkerModal: React.FC<PeopleSearchWorkerModalProps> = ({
  isOpen,
  worker,
  onClose,
  onContact,
}) => {
  const rawWorker = worker?.raw ?? {};
  const initials = useMemo(() => {
    if (!worker) return "";
    const words = worker.displayName.trim().split(" ").filter(Boolean);
    if (words.length === 0) return "";
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase();
  }, [worker]);

  const zoneEntries = useMemo(() => {
    if (!worker) return [] as Array<{ id: number; city: string; jurisdiction: string }>;
    const zones = Array.isArray((rawWorker as any).jobBoardZones) ? (rawWorker as any).jobBoardZones : [];
    return zones.map((zone: any, index: number) => ({
      id: zone?.id ?? index,
      city: zone?.city ?? "",
      jurisdiction: zone?.jurisdiccion?.name ?? zone?.jurisdiction ?? "",
    }));
  }, [rawWorker, worker]);

  const formatDate = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("es-AR");
  };

  const infoItems = useMemo(() => {
    if (!worker) return [] as Array<{ label: string; value: string }>;
    const currentJob = (rawWorker as any).current_job;
    const unemployment = worker.unemploymentFund;
    const items: Array<{ label: string; value: string | null }> = [
      {
        label: "Empleo actual",
        value:
          typeof currentJob === "boolean"
            ? currentJob
              ? "Sí"
              : "No"
            : null,
      },
      {
        label: "Fondo de desempleo",
        value:
          typeof unemployment === "boolean"
            ? unemployment
              ? "Sí"
              : "No"
            : null,
      },
      {
        label: "Fin de contrato",
        value: formatDate((rawWorker as any).employment_end_date),
      },
      {
        label: "Última actualización",
        value: formatDate((rawWorker as any).updated_at),
      },
      {
        label: "Registrado",
        value: formatDate((rawWorker as any).created_at),
      },
    ];
    return items.filter((item) => item.value).map((item) => ({ label: item.label, value: item.value as string }));
  }, [rawWorker, worker]);

  const handleContact = (mode: "email" | "phone") => {
    if (!worker) return;
    if (mode === "email" && worker.contact.email) {
      window.open(`mailto:${worker.contact.email}`, "_blank");
      return;
    }
    if (mode === "phone" && worker.contact.phone) {
      window.open(`tel:${worker.contact.phone}`);
      return;
    }
    if (onContact) {
      onContact(worker);
    }
  };

  if (!worker) {
    return null;
  }

  const hasEmail = Boolean(worker.contact.email);
  const hasPhone = Boolean(worker.contact.phone);

  return (
    <ModalOverlay isOpen={isOpen} onClose={onClose} zIndex={40}>
      <div className={styles.container} role="dialog" aria-modal="true">
        <div className={styles.header}>
          <div className={styles.identity}>
            <div className={styles.avatar}>
              <span>{initials}</span>
            </div>
            <div className={styles.titleGroup}>
              <h2 className={styles.name}>{worker.displayName}</h2>
              <div className={styles.badges}>
                <span className={styles.badge}>Trabajador</span>
                {worker.hasIdentityValidated && (
                  <span className={styles.badge}>
                    <ShieldCheck size={14} />
                    Identidad validada
                  </span>
                )}
              </div>
            </div>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className={styles.body}>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Contacto</h3>
            <div className={styles.contactActions}>
              {hasEmail && (
                <button
                  type="button"
                  className={`${styles.contactButton} ${styles.contactButtonPrimary}`}
                  onClick={() => handleContact("email")}
                >
                  <Mail size={16} />
                  Enviar correo
                </button>
              )}
              {hasPhone && (
                <button
                  type="button"
                  className={`${styles.contactButton} ${styles.contactButtonSecondary}`}
                  onClick={() => handleContact("phone")}
                >
                  <Phone size={16} />
                  Llamar
                </button>
              )}
            </div>
            <div className={styles.metadata}>
              {worker.contact.email && <span>{worker.contact.email}</span>}
              {worker.contact.phone && <span>{worker.contact.phone}</span>}
              {worker.availability && <span>{worker.availability}</span>}
            </div>
          </section>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Especialidades</h3>
            <div className={styles.chipList}>
              {worker.skills.length > 0 ? (
                worker.skills.map((skill) => (
                  <span key={skill.id} className={styles.chip}>
                    <Sparkles size={14} />
                    {skill.name}
                  </span>
                ))
              ) : (
                <span className={styles.content}>Sin especialidades cargadas.</span>
              )}
            </div>
          </section>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Ubicaciones</h3>
            <div className={styles.chipList}>
              {worker.jurisdictions.length > 0 ? (
                worker.jurisdictions.map((jur) => (
                  <span key={jur.id} className={styles.chip}>
                    <MapPin size={14} />
                    {jur.name}
                  </span>
                ))
              ) : (
                <span className={styles.content}>Sin ubicaciones definidas.</span>
              )}
            </div>
          </section>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Zonas de trabajo</h3>
            {zoneEntries.length > 0 ? (
              <div className={styles.zoneList}>
                {zoneEntries.map((zone) => (
                  <div key={zone.id} className={styles.zoneItem}>
                    {zone.city && <span className={styles.zoneCity}>{zone.city}</span>}
                    {zone.jurisdiction && (
                      <span className={styles.zoneJurisdiction}>{zone.jurisdiction}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <span className={styles.content}>Sin zonas especificadas.</span>
            )}
          </section>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Información laboral</h3>
            {infoItems.length > 0 ? (
              <div className={styles.infoGrid}>
                {infoItems.map((item) => (
                  <div key={item.label} className={styles.infoItem}>
                    <span className={styles.infoLabel}>{item.label}</span>
                    <span className={styles.infoValue}>{item.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <span className={styles.content}>Sin datos adicionales.</span>
            )}
          </section>
        </div>
      </div>
    </ModalOverlay>
  );
};

export default PeopleSearchWorkerModal;
